import { resolve, type Block, type Link, type Text } from '@ksa/model'
import { Ltr, SiteLink, SiteText, hasText, useRender } from '../context.tsx'

type LinkItem = { label: Text; link: Link }
type Branch = { name: Text; address: Text }
type Social = { network: string; link: Link }
type Props = {
  tagline: Text
  links: LinkItem[]
  branches: Branch[]
  social: Social[]
  showCopyright: boolean
}

const NETWORK_LABEL: Record<string, string> = {
  instagram: 'Instagram',
  x: 'X',
  tiktok: 'TikTok',
  snapchat: 'Snapchat',
  whatsapp: 'WhatsApp',
  linkedin: 'LinkedIn',
}

export function Footer({ block }: { block: Block }) {
  const { site, lang } = useRender()
  const p = block.props as unknown as Props
  const compact = block.variant === 'compact'
  const links = (p.links ?? []).filter((l) => hasText(l.label))
  const branches = (p.branches ?? []).filter((b) => hasText(b.name) || hasText(b.address))
  const social = (p.social ?? []).filter((s) => s.link && s.link.kind !== 'none')
  const t = (en: string, ar: string) => (lang === 'ar' ? ar : en)

  /**
   * ⚠️ THE YEAR IS COMPUTED, NOT STORED. The field is a boolean, not a text box, precisely so that a
   * customer cannot type "© 2026 Ahmed" into their footer and publish a site that is wrong on the
   * first of January and stays wrong for twelve months without anybody noticing.
   *
   * ⚠️ AND IT IS COMPUTED AT PUBLISH, NOT AT VISIT. A published page is static HTML in R2, so this
   * runs once, on the server, on the day the customer pressed Publish. A site published in December
   * therefore says the old year in January — which is a real limitation and the honest one: the
   * alternative is JavaScript on every published page to update a date, and that is not a trade
   * worth making for a footer. Re-publishing fixes it, and the renewal cycle re-publishes anyway.
   */
  const year = new Date().getFullYear()

  return (
    <footer className="s-footer">
      <div className="s-wrap">
        {!compact && (
          <div className="s-footer__cols">
            <div className="s-stack">
              <p className="s-h3">
                <SiteText value={site.title} />
              </p>
              {hasText(p.tagline) && (
                <p className="s-small" style={{ color: 'var(--muted)' }}>
                  <SiteText value={p.tagline} />
                </p>
              )}
            </div>

            {links.length > 0 && (
              <nav aria-label={t('Footer', 'التذييل')}>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} className="s-stack">
                  {links.map((l, i) => (
                    <li key={i}>
                      <SiteLink link={l.link} className="s-link">
                        <SiteText value={l.label} />
                      </SiteLink>
                    </li>
                  ))}
                </ul>
              </nav>
            )}

            {branches.length > 0 && (
              <div className="s-stack">
                <p className="s-small" style={{ fontWeight: 600 }}>
                  {t('Where to find us', 'أين تجدنا')}
                </p>
                {branches.map((b, i) => (
                  <p key={i} className="s-small" style={{ color: 'var(--muted)' }}>
                    <strong style={{ color: 'var(--on-surface)' }}>
                      <SiteText value={b.name} />
                    </strong>
                    {hasText(b.address) && (
                      <>
                        {' — '}
                        <SiteText value={b.address} />
                      </>
                    )}
                  </p>
                ))}
              </div>
            )}

            {social.length > 0 && (
              <div className="s-stack">
                <p className="s-small" style={{ fontWeight: 600 }}>
                  {t('Follow', 'تابعنا')}
                </p>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} className="s-stack">
                  {social.map((s, i) => (
                    <li key={i}>
                      <SiteLink link={s.link} className="s-link">
                        {NETWORK_LABEL[s.network] ?? s.network}
                      </SiteLink>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {compact && hasText(p.tagline) && (
          <p className="s-small s-center" style={{ color: 'var(--muted)' }}>
            <SiteText value={p.tagline} />
          </p>
        )}

        {p.showCopyright !== false && (
          <p className="s-footer__legal">
            © {year} {resolve(site.title, lang)}
          </p>
        )}

        <ReportBadge />
      </div>
    </footer>
  )
}

/**
 * The one thing on a published page that is ours rather than the customer's.
 *
 * ⚠️ IT IS NOT A PROP AND THERE IS NO SWITCH TO TURN IT OFF, which is the entire reason it is worth
 * having. This platform hands out a hostname and hosting for 5 ﷼ a month; the report link is how
 * somebody who lands on a site that is impersonating their bank tells us about it. A reporting route
 * a site owner can remove is a reporting route that is missing from precisely the sites that need it.
 * `showCopyright` is a customer choice; this is not.
 *
 * ⚠️ IT CARRIES THE HANDLE, so a report identifies the site without asking a stranger to describe
 * which page they were on. The handle is already in the hostname they are looking at — nothing is
 * revealed that the address bar does not already show.
 *
 * ⚠️ `rel="nofollow"` BECAUSE EVERY CUSTOMER SITE LINKS HERE. Thousands of footer links pointing at
 * one domain is the shape of a link scheme whatever our intent, and being demoted for it would take
 * the product's own marketing page with it.
 *
 * ⚠️ AND THE DOMAIN GOES THROUGH `<Ltr>`, WHICH ALREADY EXISTS FOR THIS. `ksa-web.app` is an
 * identifier, not prose: in an Arabic footer the bidi algorithm reorders it to `app.ksa-web`. That
 * exact defect shipped on this product's own wordmark — see CLAUDE.md §7. The component is used
 * rather than its class hand-written, so there is one declaration of what isolation means.
 */
function ReportBadge() {
  const { site, lang, builderOrigin } = useRender()
  const t = (en: string, ar: string) => (lang === 'ar' ? ar : en)
  const reportUrl = `${builderOrigin}/report?site=${encodeURIComponent(site.handle)}`

  return (
    <p className="s-footer__badge">
      <a href={builderOrigin} rel="nofollow noopener" target="_blank">
        {t('Built with', 'صُنع بـ')} <Ltr>ksa-web.app</Ltr>
      </a>
      <span aria-hidden="true"> · </span>
      <a href={reportUrl} rel="nofollow noopener" target="_blank">
        {t('Report this site', 'الإبلاغ عن هذا الموقع')}
      </a>
    </p>
  )
}
